import { useState, useEffect } from "react";
import styled from "styled-components";
import BasicTitle from "./title";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import Api from "../libs/api";
import Storage from "../libs/storage";

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.2s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const UpgradeModalContainer = styled.div`
    position: relative;
    display: flex;
    width: 600px;
    padding: 40px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    border-radius: 8px;
    background: var(--Light-Pure, #fff);
    animation: slideUp 0.3s ease-in-out;

    @keyframes slideUp {
        from {
            transform: translateY(20px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
`;

const UpgradeModalText = styled.div`
    color: #181122;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 133%;
    letter-spacing: -0.14px;
`;

const UpgradePlanBox = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    align-self: stretch;
    border-radius: 8px;
    border: 2px solid #463694;
    background: #f4f2fb;
`;

const UpgradePlanTitle = styled.div`
    color: var(--Secundary-Pure, #181122);
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const UpgradePlanPrice = styled.div`
    color: var(--Secundary-Pure, #181122);
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const DividerLine = styled.div`
    height: 1px;
    align-self: stretch;
    background: #d9d9d9;
    margin: 8px 0px;
    width: 100%;
`;

const BillingInfoText = styled.div`
    color: #909090;
    font-family: Montserrat;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
`;

const UserLimitWarning = styled.div`
    color: #181122;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
`;

const TermsLabel = styled.span`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const TermsLink = styled.a`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-decoration-line: underline;
`;

const ModalButtonsRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 16px;
`;

const LinkButton = styled(Button)`
    && {
        color: #419392;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        padding: 0px;
        line-height: 20px;
    }
`;

const PrimaryButton = styled(Button)`
    && {
        background: #57c4c2;
        color: #000;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
        border-radius: 4px;

        &:hover {
            background: #4ba09b;
        }

        &:disabled {
            background: #e4e4e4;
            color: #999;
        }
    }
`;

const ModalClose = styled.img`
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
`;

export default function ModalUpgrade({
    open,
    onClose,
    suggestedPlan,
    currentPlanName,
    activeUsersCount,
    impersonateCompanyId,
    onUpgradeSuccess,
    showErrorModal,
    showToastMessage,
    zIndex,
    setRefetchProfile,
    companyCnpj = "",
    acceptTermsOfUse = false,
}) {
    const navigate = useNavigate();
    const api = Api();
    const storage = Storage();
    const [acceptedUpgradeTerms, setAcceptedUpgradeTerms] = useState(acceptTermsOfUse);
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [planCnpj, setPlanCnpj] = useState("");
    const [planCnpjTouched, setPlanCnpjTouched] = useState(false);
    const [planCnpjError, setPlanCnpjError] = useState("");

    useEffect(() => {
        if (open) {
            setAcceptedUpgradeTerms(acceptTermsOfUse);
        }
    }, [open, acceptTermsOfUse]);

    const normalizeCnpj = (value) =>
        (value || "").toString().replace(/\D/g, "");

    const formatCnpj = (value) => {
        const digits = normalizeCnpj(value).slice(0, 14);

        const p1 = digits.slice(0, 2);
        const p2 = digits.slice(2, 5);
        const p3 = digits.slice(5, 8);
        const p4 = digits.slice(8, 12);
        const p5 = digits.slice(12, 14);

        let out = p1;
        if (digits.length > 2) out += `.${p2}`;
        if (digits.length > 5) out += `.${p3}`;
        if (digits.length > 8) out += `/${p4}`;
        if (digits.length > 12) out += `-${p5}`;
        return out;
    };

    const handleCnpjKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey) return;

        const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
            "Home",
            "End",
        ];

        if (allowedKeys.includes(e.key)) return;
        if (!/^\d$/.test(e.key)) e.preventDefault();
    };

    const isValidCnpj = (value) => {
        const cnpj = normalizeCnpj(value);
        if (cnpj.length !== 14) return false;
        if (/^(\d)\1{13}$/.test(cnpj)) return false;

        const calcDigit = (base, weights) => {
            const sum = base
                .split("")
                .reduce(
                    (acc, digit, idx) =>
                        acc + parseInt(digit, 10) * weights[idx],
                    0
                );
            const remainder = sum % 11;
            return remainder < 2 ? "0" : String(11 - remainder);
        };

        const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const d1 = calcDigit(cnpj.slice(0, 12), weights1);
        const d2 = calcDigit(cnpj.slice(0, 12) + d1, weights2);
        return cnpj.slice(-2) === d1 + d2;
    };

    const getCnpjError = (value) => {
        const digits = normalizeCnpj(value);
        if (!digits) return "CNPJ é obrigatório";
        if (!isValidCnpj(digits)) return "CNPJ inválido";
        return "";
    };

    const handleUpgrade = async () => {
        if (!suggestedPlan || !acceptedUpgradeTerms) return;

        const companyNeedsCnpj = normalizeCnpj(companyCnpj).length === 0;
        if (companyNeedsCnpj) {
            const error = getCnpjError(planCnpj);
            if (error) {
                setPlanCnpjTouched(true);
                setPlanCnpjError(error);
                return;
            }
        }

        setUpgradeLoading(true);
        try {
            const data = await storage.getItem("data");
            if (!data) return;

            const response = await api.createSubscription(
                data.token,
                suggestedPlan.id,
                impersonateCompanyId,
                normalizeCnpj(companyCnpj).length === 0
                    ? normalizeCnpj(planCnpj)
                    : null,
                acceptedUpgradeTerms
            );

            if (response.data?.createSubscription?.success) {
                const { checkoutUrl, bankSlipUrl } =
                    response.data.createSubscription;
                const paymentUrl = checkoutUrl || bankSlipUrl;

                if (paymentUrl) {
                    window.open(paymentUrl, "_blank");
                }

                setAcceptedUpgradeTerms(true);
                setRefetchProfile(true);
                onClose();

                // Chamar callback de sucesso
                if (onUpgradeSuccess) {
                    onUpgradeSuccess();
                }

                // Mostrar mensagem de sucesso
                if (showToastMessage) {
                    showToastMessage(
                        "Parabéns, seu novo plano foi configurado!",
                        "success"
                    );
                }
            } else {
                const errorMsg =
                    response.data?.createSubscription?.message ||
                    "Erro ao fazer upgrade";

                if (/CNPJ/i.test(errorMsg)) {
                    setPlanCnpjTouched(true);
                    setPlanCnpjError(errorMsg);
                    setUpgradeLoading(false);
                    return;
                }

                if (showErrorModal) {
                    showErrorModal(errorMsg);
                }
            }
        } catch (error) {
            console.error("Erro ao fazer upgrade:", error);
            if (showErrorModal) {
                showErrorModal("Erro ao fazer upgrade. Tente novamente.");
            }
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleClose = () => {
        setAcceptedUpgradeTerms(false);
        setPlanCnpj("");
        setPlanCnpjTouched(false);
        setPlanCnpjError("");
        onClose();
    };

    if (!open || !suggestedPlan) return null;

    return (
        <ModalOverlay onClick={handleClose} style={{ zIndex: zIndex || 8000 }}>
            <UpgradeModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalClose
                    src="/img/modal_close.svg"
                    onClick={handleClose}
                />
                <BasicTitle title="Fazer Upgrade" margin={0} />

                <UpgradeModalText>
                    Faça agora seu upgrade e libere quantos usuários você
                    precisar.
                </UpgradeModalText>

                {currentPlanName === "free" ? (
                    <UserLimitWarning>
                        A conta <strong>gratuita do Zag</strong> possibilita
                        ter até <strong>3 usuários</strong> e você já{" "}
                        <strong>atingiu o limite de usuários</strong>.
                    </UserLimitWarning>
                ) : (
                    <UserLimitWarning>
                        <strong>Total de usuários: {activeUsersCount}</strong>
                        <br />
                        Você já atingiu o limite de usuários para o seu plano.
                    </UserLimitWarning>
                )}

                <UpgradePlanBox>
                    <UpgradePlanPrice>
                        R$ {suggestedPlan.price.toFixed(2).replace(".", ",")}
                    </UpgradePlanPrice>
                    <UpgradePlanTitle>
                        {suggestedPlan.description}
                    </UpgradePlanTitle>
                    {currentPlanName !== "free" && (
                        <>
                            <DividerLine />
                            <BillingInfoText>
                                Sua próxima fatura já virá com o valor atualizado.
                            </BillingInfoText>
                        </>
                    )}
                </UpgradePlanBox>

                {normalizeCnpj(companyCnpj).length === 0 && (
                    <Box
                        style={{
                            width: "100%",
                        }}
                    >
                        <TextField
                            label="CNPJ da empresa *"
                            variant="outlined"
                            value={formatCnpj(planCnpj)}
                            onChange={(e) => {
                                const digits = normalizeCnpj(
                                    e.target.value
                                ).slice(0, 14);
                                setPlanCnpj(digits);
                                setPlanCnpjTouched(true);
                                setPlanCnpjError(getCnpjError(digits));
                            }}
                            onKeyDown={handleCnpjKeyDown}
                            inputProps={{
                                inputMode: "numeric",
                                maxLength: 18,
                            }}
                            error={planCnpjTouched && !!planCnpjError}
                            helperText={
                                planCnpjTouched ? planCnpjError : ""
                            }
                            style={{ width: "100%" }}
                        />
                    </Box>
                )}

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={acceptedUpgradeTerms}
                            onChange={(e) =>
                                setAcceptedUpgradeTerms(e.target.checked)
                            }
                        />
                    }
                    label={
                        <TermsLabel>
                            Li e aceito os{" "}
                            <TermsLink
                                href="https://zagapp.com.br/termos-de-uso"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                termos de uso
                            </TermsLink>
                        </TermsLabel>
                    }
                />

                <ModalButtonsRow>
                    <LinkButton
                        onClick={() => navigate("/settings?openPlans=true")}
                    >
                        CONHECER TODOS OS PLANOS
                    </LinkButton>
                    <PrimaryButton
                        disabled={
                            !acceptedUpgradeTerms ||
                            upgradeLoading ||
                            (normalizeCnpj(companyCnpj).length === 0 && !isValidCnpj(planCnpj))
                        }
                        onClick={handleUpgrade}
                    >
                        {upgradeLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "FAZER UPGRADE"
                        )}
                    </PrimaryButton>
                </ModalButtonsRow>
            </UpgradeModalContainer>
        </ModalOverlay>
    );
}
